import Phaser from 'phaser';
import { COLORS, FONTS } from '../config/theme';
import type { Player } from '../types/game';
import { getPlayerName } from '../utils/playerName';
import {
  LAYOUT_SPEC, DESIGN_SIZE,
  OPP_TILE_PITCH, OPP_CARD_THICK,
  OPP_AVATAR_ALONG_EXTENT,
  computeOpponentCard,
} from '../config/layoutSpec';
import { getDominoBackTextureKey } from '../config/dominoAssets';
import { avatarTextureKey, identityInitial } from '../config/avatars';
import { countryFlagTextureKey } from '../config/countryFlags';
// Cosmetics: durable viewer-skin plumbing (kept when the selection source
// changes). Skin is currently selected by the temporary rule in
// config/viewerCosmetics.ts — see that file for what is and
// isn't temporary about this wiring.
import type { ViewerTileSkin } from '../config/viewerCosmetics';
import { getViewerBackTextureKey } from '../config/viewerTileSkinAssets';

/**
 * DesktopPlayerCards — the three opponent displays rendered inside Phaser,
 * positioned by the layout spec (config/layoutSpec.ts).
 *
 *   top (partner, team A):        team-a-bg card, tile row along the top rail
 *   left/right (opponents, B):    team-b-bg card, tile column on the side rails
 *
 * The card HUGS its contents: the tile run is centred on the table axis and
 * collapses inward as tiles are played, and the card spans
 * union(avatar, tileRun) + padding. All three cards use one canonical
 * thickness, so at equal tile counts they are dimensionally identical.
 *
 * Geometry is design-units x k in container-local coords (offsets from the
 * table centre), so the ensemble tracks the table through any resize.
 *
 * MOBILE IS NOT AFFECTED — only instantiated in the desktop branch of
 * GameScene.create(). The local player's hand, rack and hand background are
 * owned by Hand.ts and are deliberately untouched by this class.
 */

const DESIGN_C = DESIGN_SIZE / 2; // local coord = (design - 500) * k

/** Names are truncated to this many characters, then sized as large as fits. */
const NAME_MAX_CHARS = 8;

interface PanelSpec {
  bg:     { x: number; y: number; w: number; h: number };
  avatar: { x: number; y: number; w: number; h: number };
  backTiles: {
    fixed: number; start: number; pitch: number; count: number;
    w: number; h: number; rotated: boolean;
  };
}

export class DesktopPlayerCards extends Phaser.GameObjects.Container {
  private tableSize = 0;
  private unit      = 0;
  private layoutK   = 0;

  // Cosmetics: viewer-selected tile skin (durable). Current selection
  // source is temporary — see config/viewerCosmetics.ts.
  private viewerTileSkin: ViewerTileSkin;

  constructor(scene: Phaser.Scene, viewerTileSkin: ViewerTileSkin = 'classic') {
    super(scene, 0, 0);
    this.viewerTileSkin = viewerTileSkin;
    scene.add.existing(this);
    this.setDepth(48);
  }

  /** Resolves the back texture for this viewer's skin (durable), falling
   *  back to classic if that skin's back texture failed to load. */
  private resolveBackTextureKey(): string {
    const key = getViewerBackTextureKey(this.viewerTileSkin);
    return this.scene.textures.exists(key) ? key : getDominoBackTextureKey();
  }

  /** Call once on create() and on every handleResize(). */
  setLayout(boardX: number, boardY: number, tableSize: number, unit: number, layoutK = 0): void {
    this.setPosition(boardX, boardY);
    this.tableSize = tableSize;
    this.unit      = unit;
    this.layoutK   = layoutK;
  }

  /** Call on every applyGameState() and handleResize(). Rebuilds all three cards. */
  // Per-seat child ranges recorded during update() so the tutorial spotlight
  // can measure a seat's REAL rendered card. Read-only bookkeeping — the
  // draw path itself is untouched.
  private _seatChildRanges = new Map<number, { start: number; end: number }>();

  update(
    players:     Player[],
    handCounts:  Record<string, number>,
    currentTurn: number,
    myPosition:  number,
  ): void {
    this.removeAll(true);
    this._seatChildRanges.clear();
    if (this.tableSize === 0 || this.layoutK <= 0) return;

    const draw = (spec: PanelSpec, bg: string, frame: string, seatPos: number) => {
      const start = this.list.length;
      this.drawPanel(spec, bg, frame, seatPos, players, handCounts, currentTurn);
      this._seatChildRanges.set(seatPos, { start, end: this.list.length });
    };

    // myPosition < 0 only ever reaches here for a spectator (GameScene's
    // call sites skip calling update() at all for a real participant whose
    // position hasn't resolved yet — see GameScene's `|| this._isSpectator`
    // guards) — never for a real player mid-match. A spectator has no seat
    // of their own, so there's no hand tray occupying the bottom of the
    // table: anchor at a fixed seat 0 (arbitrary but deterministic — seat
    // parity still matches real team assignment, so colors stay correct)
    // and draw all FOUR seats, including the new mirrored bottom panel.
    if (myPosition < 0) {
      const anchor = 0;
      draw(LAYOUT_SPEC.panelTop,    'team-a-bg', 'avatar-display-team-a', (anchor + 2) % 4);
      draw(LAYOUT_SPEC.panelLeft,   'team-b-bg', 'avatar-display-team-b', (anchor + 3) % 4);
      draw(LAYOUT_SPEC.panelRight,  'team-b-bg', 'avatar-display-team-b', (anchor + 1) % 4);
      draw(LAYOUT_SPEC.panelBottom, 'team-a-bg', 'avatar-display-team-a', anchor);
      return;
    }

    draw(LAYOUT_SPEC.panelTop,   'team-a-bg', 'avatar-display-team-a', (myPosition + 2) % 4);
    draw(LAYOUT_SPEC.panelLeft,  'team-b-bg', 'avatar-display-team-b', (myPosition + 3) % 4);
    draw(LAYOUT_SPEC.panelRight, 'team-b-bg', 'avatar-display-team-b', (myPosition + 1) % 4);
  }

  /** Transform-aware world bounds of one seat's rendered card, or null.
   *  Read-only geometry accessor (tutorial spotlight); no gameplay effect. */
  getSeatBounds(seatPos: number): Phaser.Geom.Rectangle | null {
    const range = this._seatChildRanges.get(seatPos);
    if (!range || range.end <= range.start) return null;
    let union: Phaser.Geom.Rectangle | null = null;
    for (let i = range.start; i < range.end; i++) {
      const child = this.list[i] as Phaser.GameObjects.Components.GetBounds & Phaser.GameObjects.GameObject;
      if (typeof child.getBounds !== 'function') continue;
      const b = child.getBounds();
      if (!(b.width > 0 && b.height > 0)) continue;
      union = union ? Phaser.Geom.Rectangle.Union(union, b) : b;
    }
    return union;
  }

  // ─── Private ──────────────────────────────────────────────────────────────

  /** Bots are identified by the payload's authoritative is_bot flag.
   *  (The old @domino.bot email check silently broke when P0 privacy removed
   *  emails from game payloads — never reintroduce it.) */
  private isBot(player: Player): boolean {
    return player.is_bot === true;
  }

  private drawPanel(
    spec:        PanelSpec,
    bgKey:       string,
    frameKey:    string, // the avatar-display frame asset, not a player avatar
    seatPos:     number,
    players:     Player[],
    handCounts:  Record<string, number>,
    currentTurn: number,
  ): void {
    const player = players.find(p => p.position === seatPos);
    if (!player) return;

    const k        = this.layoutK;
    const isActive = currentTurn === seatPos;
    const count    = Math.min(Math.max(handCounts[String(seatPos)] ?? 0, 0), spec.backTiles.count);
    const lx = (d: number) => (d - DESIGN_C) * k; // design → container-local

    // `rotated` marks the TOP panel: its long axis is X, thickness is Y.
    // Side panels are the transpose (long axis Y, thickness X).
    const rotated = spec.backTiles.rotated;

    // ── Hugging card geometry ─────────────────────────────────────────────
    // Canonical avatar extent (identical for all three panels) so the cards
    // are dimensionally identical; the avatar renders at its true asset size.
    // avatarAlongCentre tracks the tile run, so the avatar MOVES with the
    // tiles as they are played.
    const geom = computeOpponentCard(count, OPP_AVATAR_ALONG_EXTENT);

    // Across-axis centre comes from the spec's own panel placement.
    const acrossCentre = rotated ? spec.bg.y : spec.bg.x;

    const cardCX = rotated ? lx(geom.cardAlongCentre) : lx(acrossCentre);
    const cardCY = rotated ? lx(acrossCentre)         : lx(geom.cardAlongCentre);
    const cardW  = (rotated ? geom.cardAlong : OPP_CARD_THICK) * k;
    const cardH  = (rotated ? OPP_CARD_THICK : geom.cardAlong) * k;

    if (this.scene.textures.exists(bgKey)) {
      const bg = this.scene.add.image(cardCX, cardCY, bgKey);
      bg.setDisplaySize(cardW, cardH);
      this.add(bg);
    }

    // ── Avatar display frame — moves with the run ─────────────────────────
    const avX = rotated ? lx(geom.avatarAlongCentre) : lx(spec.avatar.x);
    const avY = rotated ? lx(spec.avatar.y)          : lx(geom.avatarAlongCentre);
    const avW = spec.avatar.w * k;
    const avH = spec.avatar.h * k;
    if (this.scene.textures.exists(frameKey)) {
      const av = this.scene.add.image(avX, avY, frameKey);
      av.setDisplaySize(avW, avH);
      this.add(av);
    }

    // Character avatar filling the frame's photo circle. The frame asset is a
    // WHITE circular photo well on top (~0-0.77 of its height) plus a coloured
    // name strip at the bottom — so the face fills the top circle, not the
    // whole frame.
    //
    // Player Identity V1: the face is the AUTHORITATIVE avatar_key from the
    // payload — the human's selected preset or the bot's per-session stable
    // assignment (random once at seat creation, identical on every client,
    // every round, every reconnect). No client-side picking of any kind.
    // Textures load on demand (ensureAvatarTextures); until cached — or when
    // no key / a failed load — the canonical display-name initial fills the
    // well instead, never a blank circle or broken image.
    const faceD  = avW * 0.82;                 // fill the photo well's width
    const faceCY = avY - avH * 0.114;          // centre of the white circle
    const faceKey = avatarTextureKey(player.avatar_key);
    if (faceKey && this.scene.textures.exists(faceKey)) {
      const face = this.scene.add.image(avX, faceCY, faceKey);
      face.setDisplaySize(faceD, faceD);
      this.add(face);
    } else {
      const initial = identityInitial(getPlayerName(player, `P${seatPos}`));
      const initialText = this.scene.add.text(avX, faceCY, initial, {
        fontSize:   `${Math.max(10, Math.floor(faceD * 0.46))}px`,
        color:      '#0a0a12', // dark on the frame's white photo well
        fontFamily: FONTS.accent,
        fontStyle:  '800',
      }).setOrigin(0.5, 0.5);
      this.add(initialText);
    }

    // ── Country flag chip — badge on the frame's top-left corner ──────────
    // Key is derived only from the allowlist-validated code (countryFlags.ts)
    // and drawn only when its texture is cached; unset/invalid/failed codes
    // simply keep the card's existing neutral presentation (no chip). Bots
    // never have a country, so they never show one.
    const flagKey = countryFlagTextureKey(player.country_code);
    if (flagKey && this.scene.textures.exists(flagKey)) {
      const fw = 24 * k;
      const fh = 18 * k;
      const flag = this.scene.add.image(
        avX - avW / 2 + fw / 2 + 2 * k,
        avY - avH / 2 + fh / 2 + 2 * k,
        flagKey,
      );
      flag.setDisplaySize(fw, fh);
      this.add(flag);
    }

    // ── Active-turn indicator — identical treatment on every panel ────────
    if (isActive) {
      const ring = this.scene.add.graphics();
      // Soft outer halo, then a solid gold border hugging the frame.
      ring.lineStyle(Math.max(4, 7 * k), COLORS.gold, 0.20);
      ring.strokeRoundedRect(avX - avW / 2 - 4 * k, avY - avH / 2 - 4 * k, avW + 8 * k, avH + 8 * k, 10 * k);
      ring.lineStyle(Math.max(2, 2.5 * k), COLORS.gold, 0.95);
      ring.strokeRoundedRect(avX - avW / 2 - 1.5 * k, avY - avH / 2 - 1.5 * k, avW + 3 * k, avH + 3 * k, 8 * k);
      this.add(ring);
    }

    // ── Name — on the frame's coloured bottom strip (below the photo well) ──
    const rawName = getPlayerName(player, `P${seatPos}`);
    const name    = rawName.length > NAME_MAX_CHARS
      ? rawName.slice(0, NAME_MAX_CHARS - 1) + '…'
      : rawName;

    // Slot: the coloured strip (bottom ~20% of the frame), ~85% of the width.
    const slotWpx = avW * 0.85;
    const slotHpx = avH * 0.20;
    // Largest font that fits both axes (Montserrat glyph ≈ 0.58em wide).
    const fontPx  = Math.max(
      7,
      Math.floor(Math.min(slotHpx * 0.92, slotWpx / (Math.max(1, name.length) * 0.58))),
    );
    // Centre of the coloured strip (~0.9 down the frame).
    const nameCY = avY + avH * 0.40;

    const nameText = this.scene.add.text(avX, nameCY, name, {
      fontSize:   `${fontPx}px`,
      color:      isActive ? COLORS.textGold : COLORS.textWhite,
      fontFamily: FONTS.accent,
      fontStyle:  '800',
    }).setOrigin(0.5, 0.5);
    // Thin dark stroke thickens the glyphs and keeps white legible on the
    // light-blue avatar fill. Scales with font size so it stays proportional.
    nameText.setStroke('#0a0a12', Math.max(1, Math.round(fontPx * 0.14)));
    this.add(nameText);

    // ── Face-down backs: centred run, collapsing inward ───────────────────
    const backKey = this.resolveBackTextureKey();
    if (count <= 0 || !this.scene.textures.exists(backKey)) return;

    const bt = spec.backTiles;
    for (let i = 0; i < count; i++) {
      const alongD = geom.firstTileCentre + i * OPP_TILE_PITCH;
      const along  = lx(alongD);
      const fixed  = lx(bt.fixed);

      const img = this.scene.add.image(rotated ? along : fixed, rotated ? fixed : along, backKey);
      img.setDisplaySize(bt.w * k, bt.h * k); // pre-rotation size, per the spec
      if (rotated) img.setAngle(90);
      this.add(img);
    }
  }
}
